import { DeviceType, PlayerConfig, StreamingURLServiceConfig } from '@foundation-player/core';
import { Platform, SourceType } from '@foundation-player/runtime';
import { StreamingURLServiceConfig as RemoteStreamingURLServiceConfig } from '../get-remote-config';
import { TransformPlayerConfig } from './transform-player-config';

type Placeholders = { platform: string; device?: string };

/**
 * This function is used to add the Streaming URL Service (STUS) configuration to the player config.
 *
 * @remarks
 * The player has to use different URL depending on the environment and client and thus the configuration is stored in the remote config.
 *
 * Be aware that we currently do not support all source types via STUS, because the asset aggregation is not unified yet for every source type.
 */
export const addStusConfig: TransformPlayerConfig = (
  partialBuilderConfig,
  playerConfig,
  _clientAndScope,
  _environment,
  platform,
) => {
  const { remoteConfig } = partialBuilderConfig;

  const stusConfig: PlayerConfig['streamingURLService'] = {
    endpoints: {},
  };

  const placeholders: Placeholders = {
    platform: mapToStusPlatform(platform),
    device: platform && platform !== Platform.Web ? mapPlatformToDeviceType(platform) : undefined,
  };

  // Add STUS configuration for enabled source type
  Object.values(SourceType).forEach((type) => {
    if (remoteConfig.streamingURLService && remoteConfig.streamingURLService[type]?.enable) {
      stusConfig.endpoints[type] = mapRemoteConfigFormatToPlayerConfigFormat(
        remoteConfig.streamingURLService[type],
        placeholders,
      );
    }
  });

  return {
    ...playerConfig,
    streamingURLService: {
      ...stusConfig,
      ...playerConfig.streamingURLService,
    },
  };
};

function mapPlatformToDeviceType(platform: Platform | undefined): DeviceType | undefined {
  switch (platform) {
    case Platform.WebOS3:
      return DeviceType.WebOS3;
    case Platform.WebOS4:
      return DeviceType.WebOS4;
    case Platform.WebOS5:
      return DeviceType.WebOS5;
    case Platform.WebOS6:
      return DeviceType.WebOS6;
    case Platform.WebOS22:
      return DeviceType.WebOS22;
    case Platform.WebOS23:
      return DeviceType.WebOS23;
    case Platform.Tizen3:
      return DeviceType.Tizen3;
    case Platform.Tizen4:
      return DeviceType.Tizen4;
    case Platform.Tizen5:
      return DeviceType.Tizen5;
    case Platform.Tizen5_5:
      return DeviceType.Tizen5_5;
    case Platform.Tizen6:
      return DeviceType.Tizen6;
    case Platform.Tizen6_5:
      return DeviceType.Tizen6_5;
    case Platform.Tizen7:
      return DeviceType.Tizen7;
    case Platform.Tizen8:
      return DeviceType.Tizen8;
    case Platform.MagentaG3:
      return DeviceType.MagentaG3;
    case Platform.MagentaG4:
      return DeviceType.MagentaG4;
    case Platform.MagentaG5:
      return DeviceType.MagentaG5;
    case Platform.SkyQ:
      return DeviceType.SkyQ;
    case Platform.SkyXiOne:
      return DeviceType.SkyXiOne;
    case Platform.SkyStream:
      return DeviceType.SkyStream;
    case Platform.VodafoneGTV:
      return DeviceType.VodafoneGTV;
    case Platform.VodafoneVTV:
      return DeviceType.VodafoneVTV;
    case Platform.Chromecast:
      return DeviceType.Chromecast;
    case Platform.TeleColumbus:
      return DeviceType.TeleColumbus;
    case Platform.Kepler:
      return DeviceType.Kepler;
  }
}

function mapToStusPlatform(platform: Platform | undefined): string {
  switch (platform) {
    case Platform.WebOS3:
    case Platform.WebOS4:
    case Platform.WebOS5:
    case Platform.WebOS6:
    case Platform.WebOS22:
    case Platform.WebOS23:
    case Platform.WebOS24:
    case Platform.WebOS25:
      return 'lg';
    case Platform.Tizen3:
    case Platform.Tizen4:
    case Platform.Tizen5:
    case Platform.Tizen5_5:
    case Platform.Tizen6:
    case Platform.Tizen6_5:
    case Platform.Tizen7:
    case Platform.Tizen8:
      return 'tizen';
    case Platform.MagentaG3:
    case Platform.MagentaG4:
    case Platform.MagentaG5:
      return 'magenta';
    case Platform.SkyQ:
      return 'skyq';
    case Platform.SkyXiOne:
      return 'skyxi1';
    case Platform.SkyStream:
      return 'skystream';
    case Platform.VodafoneGTV:
      return 'vodafone-gtv';
    case Platform.VodafoneVTV:
      return 'vodafone-vtv';
    case Platform.TeleColumbus:
      return 'tc';
    case Platform.Kepler:
      // There are two versions for Kepler defined in StUS, which are 'firern' and 'fireweb'.
      // These define the react native and webview variants respectively
      // Since we only support the webview variant, we use 'fireweb' here.
      return 'fireweb';
    default:
      return 'web';
  }
}

function mapRemoteConfigFormatToPlayerConfigFormat(
  config: RemoteStreamingURLServiceConfig,
  placeholders: Placeholders,
): StreamingURLServiceConfig {
  return {
    endpoint: config.endpoint ? replaceStusPlatformPlaceholder(config.endpoint, placeholders) : '',
  };
}

function replaceStusPlatformPlaceholder(endpoint: string, placeholders: Placeholders): string {
  return endpoint.replace('{platform}', placeholders.platform).replace('{device}', placeholders.device || '');
}
